<template>
  <b-field
    :label="label"
    :type="(hasError() ? 'is-danger': '')"
    :message="errorMessage()"
    class="rich-text-field"
  >
    <div class="editor">
      <editor-menu-bar
        v-slot="{ commands, isActive, getMarkAttrs }"
        :editor="editor"
      >
        <div class="menubar">
          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.bold() }"
            @click="commands.bold"
          >
            <b-icon icon="format-bold" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.italic() }"
            @click="commands.italic"
          >
            <b-icon icon="format-italic" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.strike() }"
            @click="commands.strike"
          >
            <b-icon icon="format-strikethrough" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.underline() }"
            @click="commands.underline"
          >
            <b-icon icon="format-underline" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.paragraph() }"
            @click="commands.paragraph"
          >
            <b-icon icon="format-paragraph" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.heading({ level: 1 }) }"
            @click="commands.heading({ level: 1 })"
          >
            <b-icon icon="format-header-1" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.heading({ level: 2 }) }"
            @click="commands.heading({ level: 2 })"
          >
            <b-icon icon="format-header-2" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.heading({ level: 3 }) }"
            @click="commands.heading({ level: 3 })"
          >
            <b-icon icon="format-header-3" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.link() }"
            @click="addLink(commands.link, getMarkAttrs('link'))"
          >
            <b-icon icon="link-variant" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            @click="commands.link({href: null})"
          >
            <b-icon icon="link-variant-remove" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.bullet_list() }"
            @click="commands.bullet_list"
          >
            <b-icon icon="format-list-bulleted" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.ordered_list() }"
            @click="commands.ordered_list"
          >
            <b-icon icon="format-list-numbered" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.blockquote() }"
            @click="commands.blockquote"
          >
            <b-icon icon="format-quote-close" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            @click="commands.horizontal_rule"
          >
            <b-icon icon="minus" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            @click="commands.undo"
          >
            <b-icon icon="undo" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            @click="commands.redo"
          >
            <b-icon icon="redo" />
          </button>
        </div>
      </editor-menu-bar>

      <editor-content
        class="content"
        :editor="editor"
      />
    </div>
  </b-field>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Link,
  Bold,
  Code,
  Italic,
  Strike,
  Underline,
  History,
} from "tiptap-extensions";
import InputBase from "./InputBase";
import { DialogProgrammatic as Dialog } from "buefy";

export default {
  components: {
    EditorContent,
    EditorMenuBar
  },
  extends: InputBase,
  props: {
    value: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      editor: new Editor({
        content: this.value,
        extensions: [
          new Blockquote(),
          new BulletList(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Link(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History(),
        ],
        onUpdate: ({ getHTML }) => {
          this.inputListener(getHTML());
        },
      }),
    };
  },
  watch: {
    value: {
      handler: function(value) {
        let html = this.editor.getHTML();
        if (html !== this.value) {
          this.editor.setContent(value);
        }
      }
    }
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    addLink (command, currentLink = null) {
      let href = currentLink.href || null;
      Dialog.prompt({
        message: this.$t("enter_url"),
        inputAttrs: {
          placeholder: "e.g. https://google.com",
          type: "url",
          value: href
        },
        trapFocus: true,
        confirmText: href ? this.$t("update_link") : this.$t("add_link"),
        cancelText: this.$t("cancel"),
        onConfirm: (value) => command({href: value})
      });
    }
  }
};
</script>

<style lang="scss">

.editor {
  border: 1px solid #e1e1e1;
  .menubar {
    border-bottom: 1px solid #e1e1e1;
    .button {
      border-radius: 0;
    }
    .icon i {
      font-size: 14px;
    }
  }
  .content {
    margin-bottom: 5px;
  }
  .ProseMirror {
    outline: none;
    padding: 16px;
  }
}

.rich-text-field {
  .label {
    margin-bottom: 0.75rem;
  }
}
</style>
